import { create } from 'zustand';
import type { FinancePeriod, FinancePeriodQuery } from '../pages/finance/types';

/**
 * Store global del filtro de período para el módulo Finanzas (Fase 2).
 *
 * Persiste la selección en `localStorage` con la clave `STORAGE_KEY` para que
 * el usuario reabra la pantalla con el mismo rango. La traducción a query
 * string para el backend se hace en `getQuery()` (presets → ?days=N,
 * `mtd`/custom → ?from&to).
 */

const STORAGE_KEY = 'cbt-v2-finance-period';

function loadInitialPeriod(): FinancePeriod {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.mode === 'preset' || parsed.mode === 'custom')) {
        return parsed as FinancePeriod;
      }
    }
  } catch {
    /* ignore corrupted storage */
  }
  return { mode: 'preset', preset: '30d' };
}

interface FinanceFiltersState {
  period: FinancePeriod;
  setPeriod: (p: FinancePeriod) => void;
  /** Devuelve la query a mandar al backend: { days } o { from, to } */
  getQuery: () => FinancePeriodQuery;
}

export const useFinanceFiltersStore = create<FinanceFiltersState>((set, get) => ({
  period: loadInitialPeriod(),
  setPeriod: (p) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch {
      /* ignore quota errors */
    }
    set({ period: p });
  },
  getQuery: () => {
    const p = get().period;
    if (p.mode === 'custom') return { from: p.from, to: p.to };
    if (p.preset === 'mtd') {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = now;
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      return { from: fmt(from), to: fmt(to) };
    }
    const days = p.preset === '7d' ? 7 : p.preset === '90d' ? 90 : 30;
    return { days };
  },
}));

/** Helper para componer query string desde una `FinancePeriodQuery`. */
export function periodToQs(q: FinancePeriodQuery): string {
  return 'days' in q
    ? `days=${q.days}`
    : `from=${encodeURIComponent(q.from)}&to=${encodeURIComponent(q.to)}`;
}
